import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Package, ChevronRight, Download, Repeat, Star, RotateCcw, Search, RefreshCcw } from "lucide-react";
import { api, apiErr, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import OrderStatusPill from "@/components/orders/OrderStatusPill";
import PaymentBadge from "@/components/orders/PaymentBadge";
import ReturnModal from "@/components/orders/ReturnModal";
import ReviewModal from "@/components/orders/ReviewModal";
import ReturnTrackModal from "@/components/orders/ReturnTrackModal";
import { smartReorder } from "@/lib/reorder";

const BRAND = "#d81f26";
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

const RET_STATUS = {
  requested: "Return requested",
  approved: "Return approved — pickup hoga",
  picked_up: "Return picked up",
  received: "Store par receive hua",
  qc_done: "Quality check ho gaya",
  accepted: "Return accepted",
  refund_initiated: "Refund process me hai",
  refund_completed: "Refund complete",
  refunded: "Refund complete",
  replacement_initiated: "Replacement aa raha hai",
  replacement_completed: "Replacement delivered",
  closed: "Return closed",
  rejected: "Return rejected",
};

export default function MyOrders() {
  const { user } = useAuth();
  const nav = useNavigate();
  const cart = useCart();
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState({});
  const [retModal, setRetModal] = useState(null);
  const [revModal, setRevModal] = useState(null);
  const [trackRet, setTrackRet] = useState(null);
  const [num, setNum] = useState("");
  const [phone, setPhone] = useState("");
  const [looking, setLooking] = useState(false);

  useEffect(() => { if (user === false) nav("/login?next=/orders"); }, [user, nav]);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/orders");
      setOrders(data);
      const delivered = data.filter((o) => o.status === "Delivered");
      const rets = await Promise.all(delivered.map((o) => api.get(`/orders/${o.id}/returns`).then((r) => [o.id, r.data]).catch(() => [o.id, []])));
      setReturns(Object.fromEntries(rets));
    } catch (e) { console.error("orders load failed", e); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const reorder = (o) => smartReorder(o, cart);
  const invoice = (o) => window.open(`/invoice/${o.id}`, "_blank");

  const lookup = async () => {
    if (!num.trim() || !/^\d{10}$/.test(phone.trim())) { toast.error("Order number aur 10-digit phone daalein"); return; }
    setLooking(true);
    try {
      const { data } = await api.post("/orders/lookup", { order_no: num.trim(), phone: phone.trim() });
      nav(`/orders/${data.id}`, { state: { order: data } });
    } catch (e) { toast.error(apiErr(e)); } finally { setLooking(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-5">
        <span className="w-1.5 h-6 rounded-full" style={{ backgroundColor: BRAND }} />
        <h1 className="text-2xl font-extrabold">My Orders</h1>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-20 text-slate-400" data-testid="no-orders"><Package size={40} className="mx-auto opacity-30 mb-3" />Abhi tak koi order nahi.</div>
      ) : (
        <div className="space-y-3.5">
          {orders.map((o) => {
            const rets = returns[o.id] || [];
            const ret = rets[0];
            const done = ["Delivered", "Cancelled", "Returned", "Refunded"].includes(o.status);
            return (
              <div key={o.id} data-testid={`order-row-${o.id}`} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
                <button onClick={() => nav(`/orders/${o.id}`)} className="w-full flex items-start gap-3 text-left">
                  <div className="flex -space-x-3 shrink-0">{o.items.slice(0, 3).map((it, i) => it.image ? <img key={it.product_id || i} src={it.image} alt="" className="w-11 h-11 rounded-lg object-cover border-2 border-white" /> : <div key={it.product_id || `ph-${i}`} className="w-11 h-11 rounded-lg bg-slate-100 border-2 border-white" />)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-extrabold text-sm">#{o.order_no}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{fmtDate(o.created_at)} · {o.items.length} items</p>
                    <div className="mt-1.5"><PaymentBadge method={o.payment_method} status={o.payment_status} testid={`order-pay-${o.id}`} /></div>
                  </div>
                  <OrderStatusPill status={o.status} testid={`order-status-${o.id}`} />
                </button>

                {ret ? (
                    <button data-testid={`return-status-${o.id}`} onClick={() => setTrackRet(ret)} className="mt-3 w-full flex items-center gap-2 rounded-xl border px-3 py-2 text-left" style={{ borderColor: (ret.status === "rejected" ? "#DC2626" : "#16A34A") + "55", backgroundColor: (ret.status === "rejected" ? "#DC2626" : "#16A34A") + "12" }}>
                      <RefreshCcw size={15} color={ret.status === "rejected" ? "#DC2626" : "#16A34A"} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold" style={{ color: ret.status === "rejected" ? "#DC2626" : "#16A34A" }}>{RET_STATUS[ret.status] || ret.status}</p>
                        <p className="text-[11px] text-slate-400">Return tracking dekhein ›</p>
                      </div>
                    </button>
                ) : o.status === "Delivered" ? (
                    <button data-testid={`return-btn-${o.id}`} onClick={() => setRetModal(o)} className="mt-3 w-full flex items-center justify-center gap-2 h-10 rounded-xl border-[1.5px] font-bold text-sm" style={{ borderColor: BRAND, color: BRAND }}>
                      <RotateCcw size={16} /> Return / Refund
                    </button>
                ) : null}
                {["Delivered", "Refunded", "Returned"].includes(o.status) && (
                  <button data-testid={`review-btn-${o.id}`} onClick={() => setRevModal(o)} className="mt-2 w-full flex items-center justify-center gap-2 h-10 rounded-xl border-[1.5px] border-amber-300 text-amber-600 font-bold text-sm">
                    <Star size={16} /> Rate &amp; Review
                  </button>
                )}

                <div className="flex items-center justify-between flex-wrap gap-2 border-t border-slate-100 mt-3 pt-3">
                  <span className="font-extrabold text-lg">{inr(o.total)}</span>
                  <div className="flex items-center gap-2">
                    <button data-testid={`invoice-btn-${o.id}`} onClick={() => invoice(o)} className="flex items-center gap-1 px-3 py-1.5 rounded-full border-[1.5px] text-xs font-bold" style={{ borderColor: BRAND, color: BRAND }}><Download size={14} /> Invoice</button>
                    {!done || o.status === "Delivered" ? (
                      <button data-testid={`reorder-btn-${o.id}`} onClick={() => reorder(o)} className="flex items-center gap-1 px-3 py-1.5 rounded-full border-[1.5px] text-xs font-bold" style={{ borderColor: BRAND, color: BRAND }}><Repeat size={14} /> Reorder</button>
                    ) : null}
                    <button data-testid={`track-btn-${o.id}`} onClick={() => nav(`/orders/${o.id}`)} className="flex items-center gap-0.5 text-sm font-bold" style={{ color: BRAND }}>Track <ChevronRight size={16} /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-slate-50 rounded-2xl p-5 mt-5" data-testid="order-lookup">
        <h2 className="text-lg font-bold">Track Another Order</h2>
        <p className="text-xs text-slate-500 mb-3">Order number aur phone se koi bhi order track karein.</p>
        <input data-testid="lookup-number" value={num} onChange={(e) => setNum(e.target.value)} placeholder="Order Number (e.g. RS1A2B3C4D)" className="w-full h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm mb-2 focus:outline-none focus:ring-2" style={{ "--tw-ring-color": BRAND, textTransform: "uppercase" }} />
        <input data-testid="lookup-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number used at checkout" inputMode="numeric" className="w-full h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm mb-3 focus:outline-none focus:ring-2" style={{ "--tw-ring-color": BRAND }} />
        <button data-testid="lookup-submit" onClick={lookup} disabled={looking} className="w-full h-12 rounded-xl text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ backgroundColor: BRAND }}><Search size={18} /> {looking ? "…" : "Track Order"}</button>
      </div>

      {retModal && <ReturnModal order={retModal} onClose={() => setRetModal(null)} onDone={load} />}
      {revModal && <ReviewModal order={revModal} onClose={() => setRevModal(null)} onDone={load} />}
      {trackRet && <ReturnTrackModal ret={trackRet} onClose={() => setTrackRet(null)} />}
    </div>
  );
}
